'use client';

import React, { useEffect, useMemo, useReducer, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import Image from 'next/image';

import { BasicsStep, PlanType } from './quote/BasicsStep';
import { ApplicantsStep } from './quote/ApplicantsStep';
import { GroupStep } from './quote/GroupStep';
import { ConfirmationStep } from './quote/ConfirmationStep';
import * as analytics from '../lib/analytics';
import { useWarnOnUnload } from '../hooks/use-warn-on-unload';
import { AgentQuoteHeaderFragment } from './AgentQuoteHeader.generated';
import { AgentQuoteHeader } from './AgentQuoteHeader';
import useSWRImmutable from 'swr/immutable';
import {
  OurPromiseCopyFragment,
  useQuoteFormVelocifyAgentLookupLazyQuery,
} from './QuoteForm.generated';
import { LoadingStep } from './LoadingStep';
import { ErrorStep } from './ErrorStep';
import { swrFetcher } from '../lib/swrFetcher';
import { VelocifyLookupAgentResponse } from '../pages/api/velocify-lead-lookup.page';
import { Column, Row, Section } from './Section';
import { Text } from './Text';
import { RichText } from '../lib/copyRendering';
import checkIcon from '../assets/icons/check.svg';
import {
  AgentConfirmationStepFragment,
  ConfirmationStepCopyFragment,
} from './quote/ConfirmationStep.generated';
import {
  getDeviceId,
  getSessionId,
  getUserId,
} from '@amplitude/analytics-browser';
import { apolloClient } from '../lib/apollo';

const timeoutDuration = 30 * 1000; // 30s

interface QuoteFormProps {
  agent?: AgentQuoteHeaderFragment & AgentConfirmationStepFragment;
  quoteCopy: OurPromiseCopyFragment & ConfirmationStepCopyFragment;
}

type Step =
  | 'BASICS'
  | 'APPLICANTS'
  | 'GROUP_ABOUT'
  | 'CONFIRMATION'
  | 'SUBMIT'
  | 'ERROR';

interface StateFromSteps {
  leadId?: number;
  planType?: PlanType;
  isPlanChange?: boolean;
  zipCode?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  emailAddress?: string;
}
export interface QuoteFormState {
  didError: boolean;
  step: Step;
  values: {
    velocifyAgentId?: number;
  } & StateFromSteps;
}
type QuoteFormAction =
  | {
      type: 'CONTINUE';
      values: StateFromSteps;
    }
  | {
      type: 'ERROR';
    };

export const QuoteForm: React.FC<QuoteFormProps> = ({
  agent: initialAgent,
  quoteCopy,
}) => {
  const [agentLoadingError, setAgentLoadingError] = useState(false);

  const initialState = {
    didError: false,
    step: 'BASICS' as 'BASICS',
    values: { velocifyAgentId: initialAgent?.velocifyId ?? undefined },
  };

  const [formState, dispatch] = useReducer<
    (state: QuoteFormState, action: QuoteFormAction) => QuoteFormState
  >((state, action) => {
    switch (action.type) {
      case 'CONTINUE':
        let nextStep: Step;

        switch (state.step) {
          case 'BASICS':
            if (action.values.planType === 'group') {
              nextStep = 'GROUP_ABOUT';
            } else {
              nextStep = 'APPLICANTS';
            }

            break;
          case 'APPLICANTS':
          case 'GROUP_ABOUT':
            nextStep = 'SUBMIT';

            break;
          case 'SUBMIT':
            nextStep = 'CONFIRMATION';

            break;
          default:
            nextStep = state.step;

            break;
        }

        return {
          ...state,
          step: nextStep,
          values: { ...state.values, ...action.values },
        };
      case 'ERROR':
        // Indicate something bad happened (e,g, we got a failed response from the server)

        Sentry.captureException(
          new Error('Quote form ERROR action dispatched'),
          { extra: { state } },
        );
        return {
          ...state,
          didError: true,
        };
      default:
        return state;
    }
  }, initialState);

  useEffect(() => {
    // Remove the initial state query params from the URL when continuing from the basics step

    if (formState.step !== 'BASICS') {
      const url = window.location.href.split('?');
      history.replaceState(null, '', url[0]);
    }
  }, [formState.step]);

  useEffect(() => {
    switch (formState.step) {
      // When transitioning to the "SUBMIT" step, make the corresponding API request
      case 'SUBMIT':
        const timeoutId = setTimeout(() => {
          // Submissions should only take a few seconds, show error as fallback when takes too long
          Sentry.captureException(new Error('Quote form submission timeout'), {
            extra: { timeoutDuration },
          });
          dispatch({ type: 'ERROR' });
        }, timeoutDuration);

        analytics.logEvent({
          category: 'Quote',
          name: 'Submitted form',
        });
        const body = JSON.stringify({
          ...formState.values,
          amplitudeUserId: getUserId(),
          amplitudeDeviceId: getDeviceId(),
          amplitudeSessionId: getSessionId(),
          referrer: document.referrer,
        });
        fetch('/api/quote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        })
          .then((res) => res.json())
          .then((json) => {
            clearTimeout(timeoutId);
            if (formState.step !== 'ERROR') {
              if (json.ok) {
                dispatch({ type: 'CONTINUE', values: { leadId: json.leadId } });
              } else {
                Sentry.captureException(
                  new Error(
                    'Quote form submission API function did not return { ok: true }',
                  ),
                  {
                    extra: {
                      response: json,
                    },
                  },
                );
                dispatch({ type: 'ERROR' });
              }
            }
          })
          .catch((error) => {
            clearTimeout(timeoutId);

            Sentry.captureException(
              new Error('Quote form submission request error'),
              { extra: error },
            );
            dispatch({ type: 'ERROR' });

            throw error;
          });
        break;
    }
  }, [formState.step, formState.values]);

  useEffect(() => {
    // When email has been submitted, use it as an analytics identifier
    analytics.identify(formState.values.emailAddress);
  }, [formState.values.emailAddress]);

  React.useEffect(() => {
    // Log an analytics event for every step in the form

    analytics.logEvent({
      category: 'Quote',
      name: `Started step`,
      planType: formState.values.planType,
      step: formState.step.toLowerCase(),
    });
  }, [formState.step, formState.values]);

  useEffect(() => {
    // Scroll to top whenever the step changes
    window.scrollTo(0, 0);
  }, [formState.step]);

  useWarnOnUnload(formState.step !== 'CONFIRMATION');

  const [loadVelocifyAssignedAgent, { data: velocifyAssignedAgentData }] =
    useQuoteFormVelocifyAgentLookupLazyQuery({
      onError: () => {
        setAgentLoadingError(true);
        analytics.logEvent({
          category: 'Quote',
          name: 'Contentful load error',
          leadId: formState.values.leadId,
          velocifyAgentId: formState.values.velocifyAgentId,
        });
      },
      client: apolloClient, // Manually providing this here instead of using <ApolloProvider /> to keep Apollo out of the main bundle since very few pages load data client-side
    });

  const velocifyAssignedAgent =
    velocifyAssignedAgentData?.agentCollection?.items[0];

  useSWRImmutable<VelocifyLookupAgentResponse>(
    formState.values.leadId && !formState.values.velocifyAgentId
      ? {
          url: `/api/velocify-lead-lookup?leadId=${formState.values.leadId}`,
        }
      : null,
    swrFetcher,
    {
      errorRetryInterval: 1000,
      errorRetryCount: 10,
      shouldRetryOnError: (error) => error.message !== 'Unauthorized',
      onSuccess: (data) => {
        loadVelocifyAssignedAgent({
          variables: { velocifyId: Number(data.agentId) },
        });
      },
      onError: () => {
        setAgentLoadingError(true);
        analytics.logEvent({
          category: 'Quote',
          name: 'Velocify agentId error',
          leadId: formState.values.leadId,
        });
      },
    },
  );

  const agent = initialAgent || velocifyAssignedAgent;

  const activeStep = useMemo(() => {
    switch (formState.step) {
      case 'BASICS':
        return (
          <BasicsStep
            hasAgent={!!initialAgent}
            onSubmit={(values) => {
              dispatch({
                type: 'CONTINUE',
                values,
              });

              analytics.logEvent({
                category: 'Quote',
                name: 'Completed basics step',
              });
            }}
          />
        );
      case 'APPLICANTS':
        return (
          <ApplicantsStep
            onSubmit={(values: StateFromSteps) => {
              dispatch({
                type: 'CONTINUE',
                values,
              });
              analytics.logEvent({
                category: 'Quote',
                name: 'Completed applicants step',
              });
            }}
          />
        );
      case 'GROUP_ABOUT':
        return (
          <GroupStep
            onSubmit={(values: StateFromSteps) => {
              dispatch({
                type: 'CONTINUE',
                values,
              });

              analytics.logEvent({
                category: 'Quote',
                name: 'Completed group company info step',
              });
            }}
          />
        );
      case 'SUBMIT':
      case 'CONFIRMATION':
        return formState.didError ? (
          <ErrorStep description="Error submitting quote form" />
        ) : agent || agentLoadingError ? (
          <ConfirmationStep
            planType={formState.values.planType}
            agent={agent ?? undefined}
            copy={quoteCopy}
          />
        ) : (
          <LoadingStep />
        );
      default:
        return null;
    }
  }, [
    formState.step,
    formState.values.planType,
    formState.didError,
    agent,
    initialAgent,
    agentLoadingError,
    quoteCopy,
  ]);

  return (
    <>
      {agent && <AgentQuoteHeader step={formState.step} agent={agent} />}
      <Section background="GRAY">
        <Row className="grid lg:grid-cols-[repeat(2,max-content)] gap-8 max-w-max mx-auto md:!space-x-0 !space-y-0">
          <Column className="lg:max-w-xl xl:max-w-3xl shadow rounded bg-white h-max">
            {activeStep}
          </Column>
          {formState.step === 'BASICS' && (
            <Column className="lg:max-w-xs xl:max-w-sm shadow rounded bg-white p-4 md:!p-8 h-max">
              <Text.H3 className="!text-lg md:!text-xl">
                {quoteCopy.ourPromise?.items[0]?.name}
              </Text.H3>
              <ul className="space-y-2">
                {quoteCopy.ourPromise?.items[0]?.snippetsCollection?.items.flatMap(
                  (item) =>
                    item?.__typename === 'Snippet' ? (
                      <li key={item.key} className="flex items-start">
                        <Image
                          src={checkIcon}
                          className="shrink-0 mr-4"
                          alt="Checkmark icon"
                        />
                        <div>
                          <RichText content={item.content ?? undefined} />
                        </div>
                      </li>
                    ) : (
                      []
                    ),
                )}
              </ul>
            </Column>
          )}
        </Row>
      </Section>
    </>
  );
};
